import { useState, useEffect } from 'preact/hooks';
import { getConfig, isPreviewMode } from '../config';
import { getCart, getAccount, getPaymentMethods, placeOrder, getSettings, refreshAuthAndCart } from '../api';
import { store } from '../store';
import { navigateToView } from '../navigation';
import { emit } from '../analytics';
import { t } from '../i18n';
import { translatePaymentName } from '../utils/paymentLabels';
import { TelegramPanel } from './TelegramPanel';

export function CheckoutConfirmPage() {
  const config = getConfig();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [account, setAccount] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [placeOrderError, setPlaceOrderError] = useState<string | null>(null);
  const [settings, setSettings] = useState<any>({});
  const [retentionConsent, setRetentionConsent] = useState(false);
  const [telegramCouponApplying, setTelegramCouponApplying] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [cartRes, accountRes, pmRes, settingsRes] = await Promise.all([
          getCart(config),
          getAccount(config),
          getPaymentMethods(config),
          getSettings(config)
        ]);

        if (accountRes.status === 200 && accountRes.result) {
          setAccount(accountRes.result);
        }
        if (settingsRes.status === 200 && settingsRes.result) {
          setSettings(settingsRes.result);
        }
        
        if (cartRes.result && pmRes.status === 200 && pmRes.result) {
          const pmId = (cartRes.result as any).paymentid;
          const methods = Object.entries(pmRes.result).map(([id, data]: [string, any]) => ({
             paymentid: id,
             ...data
          }));
          const selected = methods.find(m => m.paymentid == pmId);
          setPaymentMethod(selected);
        }
      } catch (err) {
        console.error('Error loading confirm data:', err);
        setError(t('confirm.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handlePlaceOrder = async () => {
    emit('sw:checkout-submit', { step: 'confirm' });
    setSubmitting(true);
    setPlaceOrderError(null);
    try {
      const pm = store.getPaymentDetails();
      const qnr = store.getOldQnrData();
      const res = await placeOrder(config, pm, qnr, { retentionConsent });
      if (res.status === 200 && res.result !== undefined && !res.error) {
        const raw = res.result as unknown;
        const data = typeof raw === 'string' ? { sorderid: raw } : (raw as { sorderid?: string; payment_link?: string });
        const hasOrder = typeof data?.sorderid === 'string' || typeof data?.payment_link === 'string';
        if (!hasOrder) {
          emit('sw:checkout-validation-error', { step: 'confirm', error_count: 1, fields: [] });
          setPlaceOrderError(res.error || t('confirm.invalidResponse'));
          store.addToast(res.error || t('confirm.invalidResponse'), 'error');
          return;
        }
        store.addToast(t('confirm.success'), 'success');
        if (data.sorderid) {
          emit('sw:order-placed', { order_id: data.sorderid });
          store.setLastOrderId(data.sorderid);
        }
        if (data.payment_link) {
          refreshAuthAndCart(config, store).catch(() => {});
          window.location.href = data.payment_link;
        } else if (data.sorderid) {
          navigateToView('order-success');
          refreshAuthAndCart(config, store).catch(() => {});
        } else {
          navigateToView('order-success');
          refreshAuthAndCart(config, store).catch(() => {});
        }
      } else {
        emit('sw:checkout-validation-error', { step: 'confirm', error_count: 1, fields: [] });
        // Ошибка: остаёмся на странице, показываем сообщение без редиректа
        const errMsg = res.error || t('confirm.failed');
        setPlaceOrderError(errMsg);
        store.addToast(errMsg, 'error');
      }
    } catch (err) {
      emit('sw:checkout-validation-error', { step: 'confirm', error_count: 1, fields: [] });
      const errMsg = err instanceof Error ? err.message : t('confirm.error');
      setPlaceOrderError(errMsg);
      store.addToast(errMsg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    const qnrVal = settings.QNR;
    const qnrEnabled = qnrVal !== 'N';
    navigateToView(qnrEnabled ? 'checkout-qnr' : 'checkout');
  };

  if (loading) return <div class="text-center py-5"><span class="spinner-border text-primary"></span></div>;
  if (error) return <div class="alert alert-danger">{error}</div>;

  return (
    <div class="shop-widget-checkout-confirm p-1">
      <h3 class="text-center mb-4 fw-bold text-success">{t('confirm.title')}</h3>
      
      <div class="card mb-4 border shadow-sm overflow-hidden">
        <div class="card-header bg-light fw-bold d-flex justify-content-between align-items-center">
          <span>{t('confirm.shippingAddress')}</span>
          <button type="button" class="btn btn-outline-primary btn-sm" onClick={() => { store.setReturnTo('checkout-confirm'); navigateToView('account', { inline: true }); }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 16 16" fill="currentColor" class="me-1" style={{ verticalAlign: '-1px' }}>
              <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325"/>
            </svg>
            {t('confirm.edit')}
          </button>
        </div>
        <div class="card-body">
          <p class="mb-1"><strong>{account?.firstname} {account?.lastname}</strong></p>
          <p class="mb-1">{account?.s_address}</p>
          <p class="mb-1">{account?.s_city}, {account?.s_state} {account?.s_zipcode}</p>
          <p class="mb-1">{account?.s_country}</p>
          <p class="mb-1">
            <span style="display:inline-flex;align-items:center;gap:.35rem">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M6.5 2.5h3L11 6l-2 2c1 2 2.5 3.5 4.5 4.5l2-2 3.5 1.5v3c0 1-1 1.8-2 1.7-7-.7-12.5-6.2-13.2-13.2-.1-1 .7-2 1.7-2z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
              </svg>
              Contact phone: {account?.phone}
            </span>
          </p>
          <p class="mb-0">
            <span style="display:inline-flex;align-items:center;gap:.35rem">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.8" />
                <path d="M4 7l8 6 8-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Contact email: {account?.email}
            </span>
          </p>
        </div>
      </div>

      <div class="card mb-4 border shadow-sm overflow-hidden">
        <div class="card-header bg-light fw-bold">{t('confirm.paymentMethod')}</div>
        <div class="card-body">
          <p class="mb-0 fw-bold text-primary">
            {paymentMethod ? translatePaymentName(paymentMethod.payment_name, paymentMethod.type, paymentMethod.paymentid) : t('confirm.notSelected')}
          </p>
        </div>
      </div>

      <TelegramPanel
        variant="order_fc"
        config={config}
        onApplyingChange={setTelegramCouponApplying}
        onCouponApplied={() => refreshAuthAndCart(config, store)}
      />

      <div class="small border rounded bg-light p-3 mb-3">
        <p class="mb-2">
          {t('confirm.shippingNote')}<br/>
          {t('confirm.overseasShippingNote')}<br/>
          {t('confirm.manufacturerNote')}
        </p>
        <div class="form-check">
          <input class="form-check-input" type="checkbox" checked disabled />
          <label class="form-check-label">
            {t('confirm.agreePolicy')}
          </label>
        </div>
        {settings?.subscribe?.checkout_consent_enabled && (
          <div class="form-check mt-2">
            <input class="form-check-input" type="checkbox" id="retention-consent" checked={retentionConsent} onChange={(e) => setRetentionConsent((e.target as HTMLInputElement).checked)} />
            <label class="form-check-label" htmlFor="retention-consent">
              {t('confirm.retentionConsent')}
            </label>
          </div>
        )}
      </div>

      {placeOrderError && (
        <div class="alert alert-danger d-flex align-items-center mt-3" role="alert">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flex: '0 0 auto' }}>
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
            <path d="M12 7v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
            <circle cx="12" cy="16.5" r="1" fill="currentColor" />
          </svg>
          <span>{placeOrderError}</span>
        </div>
      )}

      {isPreviewMode() && (
        <div class="alert alert-warning py-2 px-3 mt-3 mb-0 small">
          {t('confirm.previewModeNotice')}
        </div>
      )}
      <div class="d-flex justify-content-between mt-4">
        <button class="btn btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={handleBack}>
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M15 8a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 0 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 7.5H14.5A.5.5 0 0 1 15 8z"/>
          </svg>
          {t('confirm.back')}
        </button>
        <button class="btn btn-primary btn-lg px-5" onClick={handlePlaceOrder} disabled={submitting || telegramCouponApplying || isPreviewMode()}>
          {submitting ? t('confirm.placing') : t('confirm.placeOrder')}
        </button>
      </div>
    </div>
  );
}
